import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  TransactionBulkCardValues,
  TransactionBulkItemInterface,
} from "@interfaces/index";
import { transactionBulkCardSchema, formatAmountOnBlur } from "@utils/index";
import { useFileUpload } from "@hooks/index";
import {
  FormInput,
  FormText,
  Button,
  TransactionReceipts,
  TrashSvg,
} from "@components/index";

export type TransactionBulkCardErrors = Partial<
  Record<"amount" | "description" | "files", string>
>;

// What the parent needs from a card to submit the whole batch
export interface TransactionBulkCardApi {
  validate: () => Promise<boolean>;
  getValues: () => TransactionBulkItemInterface;
  isUploading: () => boolean;
  setErrors: (errors: TransactionBulkCardErrors) => void;
}

export const TransactionBulkCard = ({
  cardId,
  index,
  initialValues = { amount: "", description: "" },
  isDuplicated,
  isDescriptionRequired,
  canRemove,
  disabled = false,
  onChange,
  onRemove,
  onRegister,
}: {
  cardId: string;
  index: number;
  initialValues?: TransactionBulkCardValues;
  isDuplicated: boolean;
  isDescriptionRequired: boolean;
  canRemove: boolean;
  disabled?: boolean;
  onChange: (cardId: string, values: TransactionBulkCardValues) => void;
  onRemove: (cardId: string) => void;
  onRegister: (cardId: string, api?: TransactionBulkCardApi) => void;
}) => {
  const [filesError, setFilesError] = useState("");

  const {
    register,
    formState,
    trigger,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<TransactionBulkCardValues>({
    defaultValues: initialValues,
    resolver: yupResolver(transactionBulkCardSchema),
    context: { isDescriptionRequired },
  });

  const { files, isUploading, selectFiles, removeFile } = useFileUpload(() =>
    setFilesError(""),
  );

  // The registered api is created once, so it reads the latest state via refs
  const filesRef = useRef(files);
  filesRef.current = files;
  const isUploadingRef = useRef(isUploading);
  isUploadingRef.current = isUploading;

  useEffect(() => {
    onRegister(cardId, {
      validate: () => trigger(),
      getValues: () => ({
        amount: getValues("amount").replaceAll(/[^0-9/.]*/g, "") || "0",
        description: getValues("description"),
        files: filesRef.current,
      }),
      isUploading: () => isUploadingRef.current,
      setErrors: ({ amount, description, files }) => {
        if (amount) {
          setError("amount", { message: amount });
        }
        if (description) {
          setError("description", { message: description });
        }
        setFilesError(files || "");
      },
    });
    return () => onRegister(cardId);
  }, [cardId, onRegister, trigger, getValues, setError]);

  // Back to a single card: an empty description is fine again
  useEffect(() => {
    if (!isDescriptionRequired && !getValues("description")) {
      clearErrors("description");
    }
  }, [isDescriptionRequired, getValues, clearErrors]);

  useEffect(() => {
    const { unsubscribe } = watch(({ amount = "", description = "" }) =>
      onChange(cardId, { amount, description }),
    );
    return unsubscribe;
  }, [watch, cardId, onChange]);

  return (
    <div
      className="flex flex-col rounded-lg border border-black-16 p-4"
      data-testid={`card_transaction_${index}`}
    >
      <div className="flex h-8 items-center justify-between">
        <div className="text-200 font-bold text-black-70">
          Transaction {index + 1}
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="text"
            height="xsmall"
            onlyIcon={<TrashSvg />}
            disabled={disabled}
            dataTestid={`button_removeTransaction_${index}`}
            onClick={() => onRemove(cardId)}
          />
        )}
      </div>
      <div className="pt-4">
        <FormInput
          {...register("amount")}
          name={`amount_${cardId}`}
          label="Amount"
          type="text"
          maxLength={13}
          disabled={disabled}
          data-testid={`input_amount_${index}`}
          error={formState.errors.amount?.message}
          onChange={({ currentTarget }) => {
            setValue("amount", currentTarget.value);
            clearErrors("amount");
          }}
          onBlur={({ currentTarget }) =>
            setValue("amount", formatAmountOnBlur(currentTarget.value))
          }
        />
      </div>
      <div className="pt-6">
        <FormText
          {...register("description")}
          name={`description_${cardId}`}
          label="Description"
          disabled={disabled}
          data-testid={`input_description_${index}`}
          error={
            formState.errors.description?.message ||
            (isDuplicated
              ? "The Description must be unique across all transactions."
              : undefined)
          }
          onChange={({ currentTarget }) => {
            setValue("description", currentTarget.value);
            clearErrors("description");
          }}
          className="min-h-[96px]"
        />
      </div>
      <TransactionReceipts
        name={`files_${cardId}`}
        dataTestid={`input_files_${index}`}
        files={files}
        disabled={disabled}
        uploading={isUploading}
        error={filesError}
        className="pt-6"
        onSelectFiles={selectFiles}
        onRemoveFile={disabled ? undefined : removeFile}
      />
    </div>
  );
};
